import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api/base';

interface CreatePartnerProps {
  onSuccess?: (partner: any) => void;
  onCancel?: () => void;
}

function CreatePartner({ onSuccess, onCancel }: CreatePartnerProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const validateName = (value: string): string | null => {
    if (!value.trim()) return null;
    if (value.length < 3) return 'Name must be at least 3 characters';
    if (value.length > 50) return 'Name must be at most 50 characters';
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
      return 'Name can only contain letters, numbers, underscores, and dashes';
    }
    return null;
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setNameError(validateName(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    // Validate name before submission
    const validationError = validateName(name);
    if (validationError) {
      setNameError(validationError);
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.post('partners', {
        name: name.trim(),
        description: description.trim() || undefined,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Failed to create partner');
      }

      setResult(data);
      setName('');
      setDescription('');

      // Show toast notification - note: resources are still being provisioned
      toast.success(`Partner "${data.partner?.name}" is being set up`);

      // Close modal and trigger refresh
      if (onSuccess) {
        onSuccess(data.partner);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      toast.error(`Failed to create partner: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onCancel) {
      onCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex justify-center items-center z-[1000]"
      onClick={handleOverlayClick}
    >
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg relative w-[90%] max-w-[600px] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-5 pt-5 mb-5">
          <h2 className="m-0 text-slate-900 dark:text-slate-100 text-2xl font-semibold">Create New Partner</h2>
          {onCancel && (
            <button
              className="bg-transparent border-none text-2xl cursor-pointer text-slate-500 dark:text-slate-400 p-0 w-[30px] h-[30px] flex items-center justify-center hover:text-slate-900 dark:hover:text-slate-100"
              onClick={onCancel}
            >
              ×
            </button>
          )}
        </div>

        <div className="px-5 pb-5">
          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label htmlFor="partnerName" className="block mb-1 font-medium text-slate-900 dark:text-slate-100 text-sm">
                Partner Name *
              </label>
              <input
                id="partnerName"
                name="partnerName"
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
                minLength={3}
                maxLength={50}
                placeholder="my-partner-name"
                className={`w-full px-3 py-2.5 border rounded text-sm transition-colors bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25 ${nameError ? 'border-danger-500' : 'border-slate-300 dark:border-slate-600'}`}
                autoFocus
              />
              {nameError && <span className="block mt-1 text-xs text-danger-500">{nameError}</span>}
              <small className="block mt-1 text-xs text-slate-500 dark:text-slate-400">
                Letters, numbers, underscores, and dashes only (3-50 characters).
                This name is <strong>permanent</strong> and will be used to create the partner's S3 bucket.
                It cannot be changed after creation.
              </small>
            </div>

            <div className="mb-5">
              <label htmlFor="description" className="block mb-1 font-medium text-slate-900 dark:text-slate-100 text-sm">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description for this partner"
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded text-sm transition-colors bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
              />
              <small className="block mt-1 text-xs text-slate-500 dark:text-slate-400">
                Optional description (max 500 characters)
              </small>
            </div>

            {error && (
              <div className="px-4 py-3 bg-danger-500/10 text-danger-500 border border-danger-500 rounded mb-5">
                <strong>Error:</strong> {error}
              </div>
            )}

            {result && (
              <div className="px-4 py-3 bg-success-500/10 text-success-500 border border-success-500 rounded mb-5">
                <strong>Setting Up:</strong> Partner "{result.partner?.name}" is being provisioned.
                <div className="my-2 p-2.5 bg-slate-100 dark:bg-slate-700 rounded border border-success-500">
                  <p className="my-1 text-[13px] text-slate-900 dark:text-slate-100"><strong>ID:</strong> {result.partner?.partner_id}</p>
                  <p className="my-1 text-[13px] text-slate-900 dark:text-slate-100"><strong>Status:</strong> Onboarding workflow in progress</p>
                </div>
                <small className="block mt-2.5 text-[11px] italic opacity-80">
                  AWS resources (S3 bucket, KMS key) are being provisioned. Status will update when complete.
                </small>
              </div>
            )}

            <div className="flex justify-end gap-2.5 pt-5 border-t border-slate-200 dark:border-slate-600 mt-5">
              <button
                type="button"
                className="bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-none px-5 py-2.5 rounded cursor-pointer text-sm transition-colors hover:bg-slate-300 dark:hover:bg-slate-500 disabled:bg-slate-400 disabled:cursor-not-allowed"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-primary-600 text-white border-none px-5 py-2.5 rounded cursor-pointer text-sm transition-colors hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
                disabled={loading || !name.trim() || !!nameError}
              >
                {loading ? 'Creating...' : 'Create Partner'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreatePartner;
